import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole } from '../../lib/roles';
import { activeTab, getWorkspace, visibleTabs } from './settingsNavConfig';

/**
 * A Settings workspace: a tab strip over the tab's page.
 *
 * Mounted as a pathless layout route, so each tab keeps the URL its page had
 * before workspaces existed (`/settings/locations`, not
 * `/settings/organization/locations`) and nothing redirects. A workspace with a
 * single visible tab renders no strip.
 *
 * `titled` is the Safety workspace's shape (SafetyWorkspace.tsx): the workspace
 * owns the one h1, and its tab pages render without a title band or back arrow
 * of their own. EVERY WORKSPACE IS TITLED NOW — AI & integrations and Activity
 * were the last two on the older shape, where each page kept its own title and
 * its own "< Settings" arrow, which on desktop stacked a third way out under
 * the band's arrow and the sidebar.
 *
 * The prop stays rather than becoming the only behaviour, because the untitled
 * branch is what SettingsWorkspaces.test.tsx uses as its control: "renders no
 * heading of its own" is only evidence if the same component can still be asked
 * to render one.
 *
 * Tabs are filtered by the same vertical and admin rules as the sidebar, from
 * the same list (settingsNavConfig.ts).
 */
export default function SettingsWorkspaceTabs({ workspace, titled = false }: { workspace: string; titled?: boolean }) {
  const { pathname } = useLocation();
  const { hasVertical, user } = useAuth();
  const ws = getWorkspace(workspace);
  const tabs = visibleTabs(ws, { hasVertical, isAdmin: isAdminRole(user?.role) });
  const selected = activeTab(tabs, pathname);

  const body = (
    <>
      {tabs.length > 1 && (
        <div className="px-6 pt-4 overflow-x-auto">
          <div className="flex items-center gap-1 border-b border-border min-w-max" role="tablist" aria-label={`${ws.label} settings`}>
            {tabs.map((t) => {
              const active = t === selected;
              return (
                <Link
                  key={t.route}
                  to={t.route}
                  role="tab"
                  aria-selected={active}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                    active
                      ? 'border-fieldiq-accent text-fieldiq-accent'
                      : 'border-transparent text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {t.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
      <Outlet />
    </>
  );

  if (!titled) return body;

  return (
    <div className="min-h-full">
      <div className="border-b border-border px-6 py-3">
        <h1 className="text-2xl font-bold text-foreground">{ws.label}</h1>
      </div>
      {body}
    </div>
  );
}
